// SPDX-License-Identifier: GPL-3.0-or-later
// The plunger charge tone. It is one long voice, not a one-shot. Its pitch follows the strength of
// the pull. It fades out by itself when the charge events stop, and `release` ends it at once.

/** The charge tone. */
export interface Charger {
  /** Set the strength of the pull, 0 to 1. The first call starts the tone. */
  update(strength: number, now: number): void;
  /** End the tone (the plunger launched). */
  release(now: number): void;
  /** True while the tone can still be heard. */
  active(now: number): boolean;
}

/** Lowest pitch of the tone, in Hz. */
export const CHARGE_LOW_HZ = 160;
/** The tone rises two octaves over the full pull. */
export const CHARGE_OCTAVES = 2;
/** Seconds that the tone holds when no new charge event comes. */
export const CHARGE_HOLD = 0.6;
const CHARGE_FADE = 0.1;
const CHARGE_LEVEL = 0.22;
const RELEASE_FADE = 0.03;
/** Seconds between the end of a fade and the stop of the oscillator. */
const STOP_MARGIN = 0.03;

/** The pitch of a pull. */
export function chargeFrequency(strength: number): number {
  const s = Number.isFinite(strength) ? Math.min(1, Math.max(0, strength)) : 0;
  return CHARGE_LOW_HZ * 2 ** (CHARGE_OCTAVES * s);
}

interface Live {
  readonly osc: OscillatorNode;
  readonly gain: GainNode;
  until: number;
}

/** Make the charger. `dest` is the sfx bus. */
export function createCharger(ctx: BaseAudioContext, dest: AudioNode): Charger {
  let live: Live | null = null;

  const start = (hz: number, now: number): Live => {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(hz, now);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(1200, now);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    osc.connect(lp);
    lp.connect(gain);
    gain.connect(dest);
    osc.start(now);
    return { osc, gain, until: now };
  };

  const hold = (l: Live, now: number): void => {
    const g = l.gain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(CHARGE_LEVEL, now + 0.03);
    g.setValueAtTime(CHARGE_LEVEL, now + CHARGE_HOLD);
    g.linearRampToValueAtTime(0, now + CHARGE_HOLD + CHARGE_FADE);
    l.until = now + CHARGE_HOLD + CHARGE_FADE;
    // The stop time always trails the last event, so a long pull never outlives its oscillator.
    l.osc.stop(l.until + STOP_MARGIN);
  };

  const isLive = (now: number): boolean => live !== null && now < live.until;

  return {
    update(strength, now) {
      const hz = chargeFrequency(strength);
      if (!live || !isLive(now)) live = start(hz, now);
      else live.osc.frequency.setTargetAtTime(hz, now, 0.04);
      hold(live, now);
    },
    release(now) {
      if (!live || !isLive(now)) return;
      const g = live.gain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + RELEASE_FADE);
      live.osc.stop(now + RELEASE_FADE + STOP_MARGIN);
      live.until = now;
      live = null;
    },
    active: isLive,
  };
}
